import { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { orderBy, take } from "lodash";

import { TagTypes } from "constants/tags";
import { AssignedTag } from "services/types/assigned-tag.type";
import { Tag } from "services/types/tags.type";

import AddAssignedTag from "./add-assigned-tag.component";
import AssignedTagsConfirmModal from "./assigned-tags-confirm-modal.component";
import TagChip from "./tag-chip.component";

type AssignedTagsListProp = {
  showDotsOnly?: boolean | string;
  dotsSize?: string;
  tags?: AssignedTag[];
  tagType: TagTypes;
  displayLimit?: number;
  sx?: SxProps;
  disableUpdateMutation?: boolean;
  onDelete?: (tag_id: number, index?: number) => void;
  onAdd?: (tag_id: number, tag: Tag) => void;
  onUpdate?: (tag: AssignedTag) => void;
  filterActiveOnly?: boolean;
  dense?: boolean;
};

export default function AssignedTagsList({
  tags,
  tagType,
  displayLimit,
  onAdd,
  showDotsOnly = false,
  dotsSize = "8px",
  disableUpdateMutation,
  onDelete,
  onUpdate,
  sx,
  filterActiveOnly = true,
  dense = false
}: AssignedTagsListProp) {
  const { displayTags, style } = useMemo(() => {
    const displayTags = !displayLimit
      ? tags
      : take(orderBy(tags || [], ["id"], ["desc"]), displayLimit);

    const style = displayTags?.length ? { ...sx, pt: 0.5 } : sx;

    return {
      displayTags,
      style
    };
  }, [displayLimit, sx, tags]);

  const hiddenCount = (tags ?? []).length - (displayTags ?? []).length;

  const [confirmationOptions, setConfirmationOptions] = useState<{
    show: boolean;
    title: string;
    tag_id?: number;
  }>({
    show: false,
    title: "",
    tag_id: undefined
  });

  const handleCancelConfirm = useCallback(() => {
    setConfirmationOptions({
      show: false,
      title: "",
      tag_id: undefined
    });
  }, []);

  const handleSubmitConfirm = useCallback(() => {
    onDelete?.(Number(confirmationOptions?.tag_id));
    handleCancelConfirm();
  }, [handleCancelConfirm, onDelete, confirmationOptions]);

  const handleDelete = useCallback(
    (tag_id: number) => {
      const tag = tags?.find((tag) => tag.id === tag_id);
      setConfirmationOptions({
        show: true,
        title: `Do you want to remove "${tag?.tag?.name || ""}" tag?`,
        tag_id
      });
    },
    [tags]
  );

  return (
    <Box sx={style}>
      {displayTags?.filter(Boolean).map((assignedTag) =>
        showDotsOnly ? (
          <Box
            key={assignedTag.id}
            sx={{
              display: "inline-block",
              borderRadius: "50%",
              marginRight: "4px",
              width: dotsSize,
              height: dotsSize,
              background: assignedTag.tag.color
            }}
          />
        ) : (
          <TagChip
            key={assignedTag.id}
            tagType={tagType}
            assignedTag={assignedTag}
            onDelete={onDelete ? handleDelete : undefined}
            onUpdate={onUpdate}
            canEdit={Boolean(onDelete)}
            disableUpdateMutation={disableUpdateMutation}
            dense={dense}
          />
        )
      )}
      {!showDotsOnly && Boolean(hiddenCount) && (
        <Typography variant="body1" display="inline-block">
          +{hiddenCount}&nbsp;more
        </Typography>
      )}
      {onAdd && (
        <AddAssignedTag
          tagType={tagType}
          existingTags={tags ?? []}
          onAdd={onAdd}
          filterActiveOnly={filterActiveOnly}
        />
      )}
      <AssignedTagsConfirmModal
        open={confirmationOptions?.show}
        title={confirmationOptions?.title}
        onCancel={handleCancelConfirm}
        onChange={handleSubmitConfirm}
      />
    </Box>
  );
}
